// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Checkbox,
  FormField,
  Input,
  Spinner,
  Textarea,
  useToast,
} from "@chotca/ui";
import { useMe } from "@chotca/api-client";
import { getSdk } from "@chotca/api-client";

export function SettingsClient() {
  const t = useTranslations("admin");
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-ink">{t("settingsTitle")}</h1>
      <ProfileSection />
      <AppConfigSection />
      <PasswordSection />
    </div>
  );
}

function ProfileSection() {
  const t = useTranslations("admin");
  const { data: me } = useMe();
  return (
    <section className="space-y-3 rounded-2xl border border-smoke bg-paper p-5">
      <h2 className="text-sm font-bold uppercase text-ash">
        {t("settingsProfile")}
      </h2>
      <p className="text-sm">
        <span className="text-ash">Email:</span>{" "}
        <span className="font-mono">{me?.email ?? "—"}</span>
      </p>
    </section>
  );
}

// Mirrors the server's appConfigUpdateSchema, but with form-friendly types:
// empty strings stand in for null (converted back on submit). minVersion is a
// dot-separated numeric version compared by the mobile force-update gate.
const appConfigSchema = z.object({
  minVersion: z
    .string()
    .trim()
    .regex(/^\d+(\.\d+)*$/, "Định dạng phiên bản không hợp lệ (vd: 1.2.0)")
    .or(z.literal("")),
  maintenance: z.boolean(),
  maintenanceMessage: z.string(),
  iosUpdateUrl: z.string().trim().url("URL không hợp lệ").or(z.literal("")),
  androidUpdateUrl: z.string().trim().url("URL không hợp lệ").or(z.literal("")),
});
type AppConfigValues = z.infer<typeof appConfigSchema>;

const APP_CONFIG_KEY = ["admin", "app-config"] as const;

function AppConfigSection() {
  const t = useTranslations("admin");
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: APP_CONFIG_KEY,
    queryFn: () => getSdk().appConfig.get(),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AppConfigValues>({
    resolver: zodResolver(appConfigSchema),
    mode: "onTouched",
    defaultValues: {
      minVersion: "",
      maintenance: false,
      maintenanceMessage: "",
      iosUpdateUrl: "",
      androidUpdateUrl: "",
    },
    // Re-sync once the server config loads (and on every refetch).
    values: config
      ? {
          minVersion: config.minVersion ?? "",
          maintenance: config.maintenance,
          maintenanceMessage: config.maintenanceMessage ?? "",
          iosUpdateUrl: config.iosUpdateUrl ?? "",
          androidUpdateUrl: config.androidUpdateUrl ?? "",
        }
      : undefined,
  });

  const save = useMutation({
    mutationFn: (values: AppConfigValues) =>
      getSdk().appConfig.update({
        // Empty inputs clear the field (null), not set it to "".
        minVersion: values.minVersion.trim() || null,
        maintenance: values.maintenance,
        maintenanceMessage: values.maintenanceMessage.trim() || null,
        iosUpdateUrl: values.iosUpdateUrl.trim() || null,
        androidUpdateUrl: values.androidUpdateUrl.trim() || null,
      }),
    onSuccess: (data) => {
      qc.setQueryData(APP_CONFIG_KEY, data);
      toast({ tone: "success", title: "Đã lưu cấu hình ứng dụng" });
    },
    onError: () => toast({ tone: "error", title: "Không lưu được cấu hình" }),
  });

  const onSubmit = handleSubmit((values) => save.mutate(values));
  const maintenanceOn = watch("maintenance");

  return (
    <form
      className="space-y-4 rounded-2xl border border-smoke bg-paper p-5"
      onSubmit={onSubmit}
      noValidate
    >
      <div>
        <h2 className="text-sm font-bold uppercase text-ash">
          {t("settingsAppConfig")}
        </h2>
        <p className="mt-1 text-xs text-ash">
          Điều khiển bắt buộc cập nhật và chế độ bảo trì của app mobile, không
          cần deploy lại.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-ash">
          <Spinner /> Đang tải cấu hình…
        </div>
      ) : null}

      <FormField
        label="Phiên bản tối thiểu (force update)"
        error={errors.minVersion?.message}
        description="Để trống = không bắt buộc cập nhật. App dưới mức này sẽ bị chặn ở màn hình cập nhật."
      >
        <Input
          placeholder="vd: 1.2.0"
          invalid={!!errors.minVersion}
          {...register("minVersion")}
        />
      </FormField>

      <Checkbox
        label="Bật chế độ bảo trì"
        description="Chặn toàn bộ app mobile bằng màn hình bảo trì."
        {...register("maintenance")}
      />

      <FormField
        label="Thông báo bảo trì"
        error={errors.maintenanceMessage?.message}
        description={
          maintenanceOn
            ? undefined
            : "Chỉ hiển thị khi bật chế độ bảo trì. Để trống dùng nội dung mặc định."
        }
      >
        <Textarea
          rows={2}
          placeholder="vd: Hệ thống đang bảo trì, vui lòng quay lại sau."
          invalid={!!errors.maintenanceMessage}
          {...register("maintenanceMessage")}
        />
      </FormField>

      <FormField
        label="Link cập nhật iOS"
        error={errors.iosUpdateUrl?.message}
        description="URL App Store mở từ nút cập nhật trên iOS."
      >
        <Input
          placeholder="itms-apps://apps.apple.com/app/idXXXXXXXXX"
          invalid={!!errors.iosUpdateUrl}
          {...register("iosUpdateUrl")}
        />
      </FormField>

      <FormField
        label="Link cập nhật Android"
        error={errors.androidUpdateUrl?.message}
        description="URL Google Play mở từ nút cập nhật trên Android."
      >
        <Input
          placeholder="https://play.google.com/store/apps/details?id=com.chotca.app"
          invalid={!!errors.androidUpdateUrl}
          {...register("androidUpdateUrl")}
        />
      </FormField>

      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending} disabled={isLoading}>
          Lưu cấu hình
        </Button>
      </div>
    </form>
  );
}

const passwordSchema = z.object({
  oldPwd: z.string().min(1, "Vui lòng nhập mật khẩu hiện tại"),
  newPwd: z
    .string()
    .min(8, "Mật khẩu mới tối thiểu 8 ký tự")
    .max(72, "Mật khẩu tối đa 72 ký tự"),
});
type PasswordValues = z.infer<typeof passwordSchema>;

function PasswordSection() {
  const t = useTranslations("admin");
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    mode: "onTouched",
    defaultValues: { oldPwd: "", newPwd: "" },
  });
  const change = useMutation({
    mutationFn: (values: PasswordValues) =>
      getSdk().auth.changePassword({
        oldPassword: values.oldPwd,
        newPassword: values.newPwd,
      }),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã đổi mật khẩu" });
      reset({ oldPwd: "", newPwd: "" });
    },
    onError: () => toast({ tone: "error", title: "Không đổi được mật khẩu" }),
  });
  const onSubmit = handleSubmit((values) => change.mutate(values));
  return (
    <form
      className="space-y-3 rounded-2xl border border-smoke bg-paper p-5"
      onSubmit={onSubmit}
      noValidate
    >
      <h2 className="text-sm font-bold uppercase text-ash">
        {t("settingsPassword")}
      </h2>
      <FormField label="Mật khẩu hiện tại" error={errors.oldPwd?.message}>
        <Input
          type="password"
          invalid={!!errors.oldPwd}
          {...register("oldPwd")}
        />
      </FormField>
      <FormField label="Mật khẩu mới" error={errors.newPwd?.message}>
        <Input
          type="password"
          invalid={!!errors.newPwd}
          {...register("newPwd")}
        />
      </FormField>
      <div className="flex justify-end">
        <Button type="submit" loading={change.isPending}>
          Đổi mật khẩu
        </Button>
      </div>
    </form>
  );
}
